import React from "react";
import {
  ActivityIndicator,
  Animated,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Globe } from "lucide-react-native";

import { themes } from "@/Components/Styling/themeConfigs";

function LoadingScreen(): React.JSX.Element {
  const currentTheme = themes.morning;
  const loadingEmojis = ["🤣", "😍", "🤭", "😊"];
  const animatedValues = React.useRef<Animated.Value[]>(
    loadingEmojis.map(() => new Animated.Value(0)),
  ).current;

  React.useEffect(() => {
    // Create animations with proper sequence per emoji
    const animations = animatedValues.map((value, index) => {
      // Add staggered delay based on index
      const delay = index * 100;

      return Animated.loop(
        Animated.sequence([
          // Move up
          Animated.timing(value, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
            delay: delay,
          }),
          // Move down
          Animated.timing(value, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
          // Add pause between loops
          Animated.delay(100),
        ]),
      );
    });

    // Start all animations in parallel after a short delay
    setTimeout(() => {
      Animated.parallel(animations).start();
    }, 100);

    // Clean up animations on unmount
    return () => {
      animations.forEach((animation) => animation.stop());
    };
  }, []);

  return (
    <LinearGradient
      colors={currentTheme.bg}
      style={styles.container}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <Text style={[styles.title, { color: currentTheme.text }]}>
        Kai Social
      </Text>
      <View style={styles.taglineContainer}>
        <Globe color={currentTheme.text} size={20} />
        <Text style={[styles.tagline, { color: currentTheme.text }]}>
          Explore. Connect. Share.
        </Text>
      </View>
      <View style={styles.emojiContainer}>
        {loadingEmojis.map((emoji, index) => (
          <Animated.Text
            key={index}
            style={[
              styles.emoji,
              {
                transform: [
                  {
                    translateY: (
                      animatedValues[index] as Animated.Value
                    ).interpolate({
                      inputRange: [0, 1],
                      outputRange: [0, -20],
                    }),
                  },
                ],
              },
            ]}
          >
            {emoji}
          </Animated.Text>
        ))}
      </View>
      <View style={styles.loadingContainer}>
        <Text style={[styles.loadingText, { color: currentTheme.text }]}>
          Loading
        </Text>

        {/* Use built-in ActivityIndicator for reliable cross-platform spinner */}
        <ActivityIndicator
          size="small"
          color={"#4299E1"}
          style={styles.spinner}
        />
      </View>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 48,
    fontWeight: "bold",
    marginBottom: 8,
  },
  emojiContainer: {
    flexDirection: "row",
    marginTop: 32,
  },
  emoji: {
    fontSize: 48,
    marginHorizontal: 8,
  },
  loadingContainer: {
    marginTop: 20,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(100, 116, 139, 0.15)",
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  loadingText: {
    fontSize: 18,
    marginRight: 8,
  },
  tagline: {
    fontSize: 20,
    left: 5,
  },
  taglineContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  spinner: {
    marginLeft: 5,
  },
});

export default LoadingScreen;
